// ============================================================================
// renderer.ts - Renders LLM JSON UI arrays into DOM elements
// ============================================================================

import type { QuestionContext } from '../profiling/db'
import { activeTypewriters } from './typewriter'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UIElement {
  // Known types + LLMs may invent arbitrary types (e.g. "probe", "divine_wisdom")
  type: string
  name: string
  label?: string
  value: string
  color?: string
  voice?: string
  min?: number | string
  max?: number | string
  step?: number | string
  options?: RadioOption[] | string[] | string
  text?: string
  placeholder?: string
  /** LLM's predicted response for this element — used as autofill default */
  predicted?: string
  /** Hidden justification: why this question is asked + what trait it measures */
  justification?: string
  /** Reactive variants — text swaps instantly when a dependent input changes */
  reactive?: {
    depends_on: string
    variants: Record<string, string>
  }
}

export interface RenderResult {
  notes: string
  subjectId: string
  analysis: string
}

interface RadioOption {
  label: string
  value: string
}

interface ParsedRadioOption {
  label: string
  value: string
  isDefault: boolean
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MIN_CONTRAST_LIGHTNESS_DARK_BG = 0.55  // min lightness for text on dark backgrounds
const MAX_CONTRAST_LIGHTNESS_LIGHT_BG = 0.45 // max lightness for text on light backgrounds

// ---------------------------------------------------------------------------
// Color utilities
// ---------------------------------------------------------------------------

function isValidHexColor(hex: unknown): hex is string {
  return typeof hex === 'string' && /^#[0-9A-F]{6}$/i.test(hex)
}

/**
 * Detect if the current page theme has a light background.
 */
function isCurrentThemeLight(): boolean {
  return !document.body.classList.contains('dark-mode')
}

/**
 * Adjusts a hex color so it has enough contrast against the current background.
 * On dark backgrounds: ensures minimum lightness (brightens dark colors).
 * On light backgrounds: ensures maximum lightness (darkens light colors).
 */
export function adjustColorForContrast(hex: string): string {
  if (!isValidHexColor(hex)) return hex

  const light = isCurrentThemeLight()

  let r = parseInt(hex.substring(1, 3), 16) / 255
  let g = parseInt(hex.substring(3, 5), 16) / 255
  let b = parseInt(hex.substring(5, 7), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }

  // On light bg, we want colors DARK enough. On dark bg, colors LIGHT enough.
  const adjustNeeded = light
    ? l > MAX_CONTRAST_LIGHTNESS_LIGHT_BG
    : l < MIN_CONTRAST_LIGHTNESS_DARK_BG

  if (!adjustNeeded) {
    // For dark bg, also cap extremely light colors (like the original logic)
    if (!light && l > 0.85) {
      return hslToHex(h, s, 0.75)
    }
    return hex
  }

  const targetL = light
    ? MAX_CONTRAST_LIGHTNESS_LIGHT_BG * 0.9  // darken for light bg
    : MIN_CONTRAST_LIGHTNESS_DARK_BG          // brighten for dark bg

  return hslToHex(h, s, targetL)
}

/**
 * Convert a hex color to rgba with the given alpha.
 */
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.substring(1, 3), 16)
  const g = parseInt(hex.substring(3, 5), 16)
  const b = parseInt(hex.substring(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Pick a readable text color (white or dark) for a given background hex color.
 * Uses WCAG relative luminance formula.
 */
function contrastTextForBg(hex: string): string {
  const r = parseInt(hex.substring(1, 3), 16) / 255
  const g = parseInt(hex.substring(3, 5), 16) / 255
  const b = parseInt(hex.substring(5, 7), 16) / 255
  const toLinear = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
  return L > 0.4 ? '#1e293b' : '#ffffff'
}

/**
 * Set CSS custom properties for the selected/checked state of an option div.
 * Uses the LLM's accent color at strong opacity for background,
 * with a WCAG-contrast text color.
 */
function setSelectionVars(optionDiv: HTMLElement, color: string): void {
  optionDiv.style.setProperty('--sel-bg', hexToRgba(color, 0.85))
  optionDiv.style.setProperty('--sel-border', color)
  optionDiv.style.setProperty('--sel-text', contrastTextForBg(color))
}

function hslToHex(h: number, s: number, l: number): string {
  const hue2rgb = (p: number, q: number, t: number): number => {
    let tt = t
    if (tt < 0) tt += 1
    if (tt > 1) tt -= 1
    if (tt < 1 / 6) return p + (q - p) * 6 * tt
    if (tt < 1 / 2) return q
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
    return p
  }

  let r1: number, g1: number, b1: number
  if (s === 0) {
    r1 = g1 = b1 = l
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r1 = hue2rgb(p, q, h + 1 / 3)
    g1 = hue2rgb(p, q, h)
    b1 = hue2rgb(p, q, h - 1 / 3)
  }

  const toHex = (x: number): string => {
    const hexVal = Math.round(x * 255).toString(16)
    return hexVal.length === 1 ? '0' + hexVal : hexVal
  }

  return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`
}

// ---------------------------------------------------------------------------
// HTML tag stripper — removes all HTML tags, preserving inner text content.
// Used to sanitize LLM-generated labels that may contain inline HTML
// (e.g. <span style='...'>) which should be displayed as plain text.
// ---------------------------------------------------------------------------

function stripHtmlTags(raw: string): string {
  return raw.replace(/<[^>]*>/g, '')
}

// ---------------------------------------------------------------------------
// Markdown-lite renderer (bold, italic, code blocks, newlines)
// ---------------------------------------------------------------------------

/** Strip unreplaced LLM template placeholders like [Subject ID], [Player Name], etc. */
const PLACEHOLDER_RE = /\[(?:Subject\s*(?:ID|Name|'s\s*Name)|Player(?:'s)?\s*Name|Your\s*Name|Insert\s*(?:Name|Subject)|Name|nickname|evolving\s+\w+)[^\]]*\]/gi

/** Detect internal labels like "focus_intensity", "trust_glyph_wall" — underscore-delimited identifiers with no spaces */
const INTERNAL_LABEL_RE = /^[A-Za-z]+(?:_[A-Za-z0-9]+)+$/

function stripPlaceholders(text: string): string {
  let cleaned = text.replace(PLACEHOLDER_RE, '').trim()
  // If the entire label is an internal identifier (2+ underscore-delimited segments, no spaces),
  // convert to readable text: "focus_intensity" → "Focus Intensity"
  if (INTERNAL_LABEL_RE.test(cleaned)) {
    cleaned = cleaned
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')  // Split camelCase
      .trim()
  }
  return cleaned
}

function renderBasicMarkdown(raw: string): string {
  return raw
    .replace(PLACEHOLDER_RE, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/```([\s\S]*?)```/g, (_match, p1: string) => `<pre>${p1.trim()}</pre>`)
    .replace(/\n/g, '<br>')
}

// ---------------------------------------------------------------------------
// Individual element renderers
// ---------------------------------------------------------------------------

function renderImageElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  adjustedColor: string | null,
): void {
  wrapper.classList.add('geems-image-container')
  wrapper.classList.remove('geems-element')
  wrapper.style.borderLeftColor = 'transparent'

  if (element.label) {
    const labelDiv = document.createElement('div')
    labelDiv.className = 'geems-label text-center font-bold text-xl mb-4'
    if (adjustedColor) labelDiv.style.color = adjustedColor
    labelDiv.textContent = stripPlaceholders(element.label)
    wrapper.appendChild(labelDiv)
  }

  const imagePrompt = element.value || 'abstract image'

  // Shimmer placeholder container
  const placeholderDiv = document.createElement('div')
  placeholderDiv.className = 'geems-image-placeholder'

  const spinnerSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  spinnerSvg.setAttribute('width', '32')
  spinnerSvg.setAttribute('height', '32')
  spinnerSvg.setAttribute('viewBox', '0 0 24 24')
  spinnerSvg.setAttribute('fill', 'none')
  spinnerSvg.classList.add('geems-image-spinner')
  const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
  circle.setAttribute('cx', '12')
  circle.setAttribute('cy', '12')
  circle.setAttribute('r', '10')
  circle.setAttribute('stroke', 'currentColor')
  circle.setAttribute('stroke-width', '3')
  circle.setAttribute('opacity', '0.2')
  const arc = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  arc.setAttribute('d', 'M4 12a8 8 0 018-8')
  arc.setAttribute('stroke', 'currentColor')
  arc.setAttribute('stroke-width', '3')
  arc.setAttribute('stroke-linecap', 'round')
  spinnerSvg.appendChild(circle)
  spinnerSvg.appendChild(arc)
  placeholderDiv.appendChild(spinnerSvg)

  const loadingText = document.createElement('p')
  loadingText.className = 'geems-image-loading-text'
  loadingText.textContent = 'Generating image...'
  placeholderDiv.appendChild(loadingText)

  wrapper.appendChild(placeholderDiv)

  // Actual image — hidden until loaded
  const img = document.createElement('img')
  img.className = 'geems-image'
  img.style.display = 'none'
  img.dataset.imagePrompt = imagePrompt
  img.alt = imagePrompt

  img.onload = () => {
    placeholderDiv.remove()
    img.style.display = 'block'
  }
  img.onerror = () => {
    const retries = parseInt(img.dataset.retryCount || '0')
    const maxRetries = 3

    // Only retry if the src has been set to a real URL (not the empty placeholder SVG)
    if (retries < maxRetries && img.src.includes('/api/image/')) {
      img.dataset.retryCount = String(retries + 1)
      loadingText.textContent = `Retrying image... (${retries + 1}/${maxRetries})`

      const delay = (retries + 1) * 2000 // 2s, 4s, 6s
      setTimeout(() => {
        try {
          // First retry: same URL (handles transient network errors like ERR_NETWORK_CHANGED)
          // Subsequent retries: change seed for a different image variant
          if (retries > 0) {
            const url = new URL(img.src, window.location.href)
            url.searchParams.set('seed', String(Math.floor(Math.random() * 2_147_483_647)))
            img.src = url.toString()
          } else {
            // Re-trigger load of same URL by toggling src
            const currentSrc = img.src
            img.src = ''
            img.src = currentSrc
          }
        } catch {
          // URL parse failed — give up
          placeholderDiv.querySelector('.geems-image-spinner')?.remove()
          loadingText.textContent = 'Image could not be loaded'
          loadingText.style.color = '#dc2626'
        }
      }, delay)
      return
    }

    // Max retries exhausted or not a retryable URL
    placeholderDiv.querySelector('.geems-image-spinner')?.remove()
    loadingText.textContent = 'Image could not be loaded'
    loadingText.style.color = '#dc2626'
  }

  // src is set to empty — the game loop's resolveImages() sets the real URL
  img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg"%3E%3C/svg%3E'

  wrapper.appendChild(img)
}

function renderInlineImageElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  _adjustedColor: string | null,
): void {
  wrapper.classList.add('geems-inline-image-container')
  wrapper.classList.remove('geems-element')
  wrapper.style.borderLeftColor = 'transparent'

  const imagePrompt = element.value || 'abstract detail'

  // Smaller placeholder for inline images
  const placeholderDiv = document.createElement('div')
  placeholderDiv.className = 'geems-inline-image-placeholder'

  const spinnerSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  spinnerSvg.setAttribute('width', '20')
  spinnerSvg.setAttribute('height', '20')
  spinnerSvg.setAttribute('viewBox', '0 0 24 24')
  spinnerSvg.setAttribute('fill', 'none')
  spinnerSvg.classList.add('geems-image-spinner')
  const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
  circle.setAttribute('cx', '12')
  circle.setAttribute('cy', '12')
  circle.setAttribute('r', '10')
  circle.setAttribute('stroke', 'currentColor')
  circle.setAttribute('stroke-width', '3')
  circle.setAttribute('opacity', '0.2')
  const arc = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  arc.setAttribute('d', 'M4 12a8 8 0 018-8')
  arc.setAttribute('stroke', 'currentColor')
  arc.setAttribute('stroke-width', '3')
  arc.setAttribute('stroke-linecap', 'round')
  spinnerSvg.appendChild(circle)
  spinnerSvg.appendChild(arc)
  placeholderDiv.appendChild(spinnerSvg)
  wrapper.appendChild(placeholderDiv)

  const img = document.createElement('img')
  img.className = 'geems-inline-image'
  img.style.display = 'none'
  img.dataset.imagePrompt = imagePrompt
  img.dataset.imageSize = 'inline' // signal for resolveImages to use smaller dimensions
  img.alt = element.label || imagePrompt

  img.onload = () => {
    placeholderDiv.remove()
    img.style.display = 'block'
  }
  img.onerror = () => {
    // Inline images fail silently — just remove placeholder
    placeholderDiv.remove()
    wrapper.remove()
  }

  img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg"%3E%3C/svg%3E'
  wrapper.appendChild(img)
}

function renderTextElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  adjustedColor: string | null,
): void {
  const textContent = element.text || element.value || ''

  // Only show a label for elements whose name does NOT indicate a narrative/analysis role
  const suppressLabel = ['narrative', 'divine_wisdom', 'player_facing_analysis'].some(
    (part) => element.name?.includes(part),
  )
  if (element.label && !suppressLabel) {
    const label = document.createElement('label')
    label.className = 'geems-label'
    if (adjustedColor) label.style.color = adjustedColor
    label.textContent = stripPlaceholders(element.label)
    wrapper.appendChild(label)
  }

  const textEl = document.createElement('div')
  textEl.className = 'geems-text'
  textEl.innerHTML = renderBasicMarkdown(textContent)

  // Reactive variants — text swaps when a dependent input changes
  if (element.reactive?.depends_on && element.reactive.variants) {
    textEl.classList.add('geems-text-reactive')
    wrapper.dataset.reactiveDependsOn = element.reactive.depends_on
    wrapper.dataset.reactiveVariants = JSON.stringify(element.reactive.variants)
    wrapper.dataset.reactiveOriginal = textContent
  }

  wrapper.appendChild(textEl)
}

function renderTextFieldElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  adjustedColor: string | null,
): void {
  const label = document.createElement('label')
  label.className = 'geems-label'
  label.textContent = stripPlaceholders(element.label || element.name)
  label.htmlFor = element.name
  if (adjustedColor) label.style.color = adjustedColor
  wrapper.appendChild(label)

  const input = document.createElement('textarea')
  input.className = 'geems-textarea'
  input.id = element.name
  input.name = element.name
  input.rows = 4
  // Autofill with LLM's predicted response
  input.value = element.predicted || element.value || ''
  input.placeholder = element.placeholder || 'Type response...'
  input.dataset.elementType = 'textfield'
  if (element.predicted) {
    input.dataset.predicted = element.predicted
    input.classList.add('geems-predicted')
  }
  if (element.justification) input.dataset.justification = element.justification
  wrapper.appendChild(input)
}

function renderCheckboxElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  adjustedColor: string | null,
): void {
  wrapper.classList.remove('geems-element')
  wrapper.style.borderLeftColor = 'transparent'
  wrapper.style.padding = '0'
  wrapper.style.marginBottom = '0.75rem'

  const optionDiv = document.createElement('div')
  optionDiv.className = 'geems-checkbox-option'
  if (adjustedColor) {
    optionDiv.style.borderColor = hexToRgba(adjustedColor, 0.25)
    optionDiv.style.backgroundColor = hexToRgba(adjustedColor, isCurrentThemeLight() ? 0.05 : 0.08)
    setSelectionVars(optionDiv, adjustedColor)
  }

  const input = document.createElement('input')
  input.type = 'checkbox'
  input.id = element.name
  input.name = element.name
  // Use predicted value if available
  const checkValue = element.predicted || element.value
  input.checked = checkValue === true as unknown || String(checkValue).toLowerCase() === 'true'
  input.dataset.elementType = 'checkbox'
  if (element.predicted) input.dataset.predicted = element.predicted
  if (element.justification) input.dataset.justification = element.justification
  if (adjustedColor) input.style.accentColor = adjustedColor

  const label = document.createElement('label')
  label.htmlFor = element.name
  label.textContent = stripPlaceholders(element.label || element.name)
  label.className = 'flex-grow cursor-pointer'

  // Make the entire option div clickable — clicks on padding/border area
  // must propagate to the checkbox input.
  optionDiv.addEventListener('click', (e) => {
    if (e.target !== input) {
      input.checked = !input.checked
      input.dispatchEvent(new Event('change', { bubbles: true }))
    }
  })

  optionDiv.appendChild(input)
  optionDiv.appendChild(label)
  wrapper.appendChild(optionDiv)
}

function renderSliderElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  adjustedColor: string | null,
): void {
  const label = document.createElement('label')
  label.className = 'geems-label'
  label.textContent = stripPlaceholders(element.label || element.name)
  label.htmlFor = element.name
  if (adjustedColor) label.style.color = adjustedColor
  wrapper.appendChild(label)

  const sliderContainer = document.createElement('div')
  sliderContainer.className = 'flex items-center space-x-4 mt-2'

  const input = document.createElement('input')
  input.type = 'range'
  input.className = 'geems-slider flex-grow'
  input.id = element.name
  input.name = element.name

  const min = parseFloat(String(element.min)) || 0
  const max = parseFloat(String(element.max)) || 10
  input.min = String(min)
  input.max = String(max)
  input.step = String(element.step || 1)

  // Use predicted value if available, otherwise fall back to element value
  const predictedValue = element.predicted ? parseFloat(element.predicted) : NaN
  const defaultValue = !isNaN(predictedValue) ? predictedValue : parseFloat(element.value)
  input.value = String(
    isNaN(defaultValue) ? (min + max) / 2 : Math.max(min, Math.min(max, defaultValue)),
  )
  input.dataset.elementType = 'slider'
  if (element.predicted) input.dataset.predicted = element.predicted
  if (element.justification) input.dataset.justification = element.justification

  if (adjustedColor) {
    input.style.accentColor = adjustedColor
    input.style.setProperty('--slider-thumb-color', adjustedColor)
  }

  const valueDisplay = document.createElement('span')
  valueDisplay.className = 'geems-slider-value-display font-medium w-auto text-right'
  valueDisplay.textContent = input.value
  if (adjustedColor) valueDisplay.style.color = adjustedColor

  input.oninput = () => {
    valueDisplay.textContent = input.value
  }

  sliderContainer.appendChild(input)
  sliderContainer.appendChild(valueDisplay)
  wrapper.appendChild(sliderContainer)
}

/**
 * Parses the options source (string, array of strings, array of objects)
 * into a uniform ParsedRadioOption array plus the default value.
 */
function parseRadioOptions(
  element: UIElement,
): { options: ParsedRadioOption[]; defaultValue: string | null } {
  let optionsSource: unknown = element.options ?? element.value
  let defaultValue: string | null = null
  let options: ParsedRadioOption[] = []

  try {
    // If the source is a JSON string, parse it
    if (typeof optionsSource === 'string') {
      try {
        optionsSource = JSON.parse(optionsSource)
      } catch {
        // Not JSON — check for newline-separated options (common LLM format)
        const str = optionsSource as string
        const lines = str.split('\n').map(l => l.trim()).filter(Boolean)
        if (lines.length > 1) {
          optionsSource = lines.map((line, i) => {
            const value = String.fromCharCode(97 + i) // a, b, c, d...
            return { label: line, value }
          })
        } else {
          optionsSource = [{ label: str, value: str }]
        }
      }
    }

    if (Array.isArray(optionsSource)) {
      options = (optionsSource as unknown[])
        .map((opt) => {
          let currentLabel = ''
          let currentValue = ''
          let isDefault = false

          if (typeof opt === 'object' && opt !== null && 'value' in opt) {
            const obj = opt as { value: unknown; label?: unknown }
            currentValue = String(obj.value)
            currentLabel = obj.label !== undefined ? String(obj.label) : currentValue
            if (currentLabel.startsWith('*')) {
              defaultValue = currentValue
              currentLabel = currentLabel.substring(1)
              isDefault = true
            }
          } else if (typeof opt === 'object' && opt !== null) {
            // Object without 'value' key — try common alternate keys
            const obj = opt as Record<string, unknown>
            const text = obj.text ?? obj.label ?? obj.name ?? obj.title ?? obj.option ?? ''
            currentLabel = String(text)
            currentValue = String(obj.id ?? text)
            if (currentLabel.startsWith('*')) {
              defaultValue = currentValue
              currentLabel = currentLabel.substring(1)
              isDefault = true
            }
          } else {
            currentValue = String(opt)
            currentLabel = currentValue
            if (currentLabel.startsWith('*')) {
              defaultValue = currentValue.substring(1)
              currentValue = defaultValue
              currentLabel = defaultValue
              isDefault = true
            }
          }

          return { value: currentValue, label: currentLabel, isDefault }
        })
        .filter((opt): opt is ParsedRadioOption => {
          if (!opt) return false
          // Filter out bare value markers (single chars like "a", "b", "c", "d")
          // that the LLM sometimes mixes into option arrays
          if (opt.label.length === 1 && /^[a-d]$/i.test(opt.label) && opt.value === opt.label) {
            return false
          }
          // Filter out broken serialization artifacts
          if (opt.label.includes('[object Object]') || opt.label === '[object Object]') {
            return false
          }
          return opt.label.length > 0
        })

      // Fallback: if element.value is a simple string matching an option, use it as default
      if (defaultValue === null && element.value && typeof element.value === 'string') {
        let isValueSimpleString = true
        try {
          if (Array.isArray(JSON.parse(element.value))) isValueSimpleString = false
        } catch {
          // not JSON, that's fine
        }
        if (isValueSimpleString) {
          const directMatch = options.find((opt) => opt.value === element.value)
          if (directMatch) defaultValue = directMatch.value
        }
      }
    }
  } catch (e) {
    console.error('Failed to parse radio options:', element.name, e)
  }

  // Default to first option if nothing marked
  if (defaultValue === null && options.length > 0) {
    defaultValue = options[0].value
  }

  return { options, defaultValue }
}

function renderRadioElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  adjustedColor: string | null,
): void {
  wrapper.classList.remove('geems-element')
  wrapper.style.borderLeftColor = 'transparent'
  wrapper.style.padding = '0'
  wrapper.style.marginBottom = '0.75rem'

  const label = document.createElement('label')
  label.className = 'geems-label block mb-2'
  label.textContent = stripPlaceholders(element.label || element.name)
  if (adjustedColor) label.style.color = adjustedColor
  wrapper.appendChild(label)

  const { options, defaultValue } = parseRadioOptions(element)
  // Override default with predicted value if available
  const selectedValue = element.predicted || defaultValue

  if (options.length > 0) {
    options.forEach((option, idx) => {
      const optionDiv = document.createElement('div')
      optionDiv.className = 'geems-radio-option'
      if (adjustedColor) {
        optionDiv.style.borderColor = hexToRgba(adjustedColor, 0.25)
        optionDiv.style.backgroundColor = hexToRgba(adjustedColor, isCurrentThemeLight() ? 0.05 : 0.08)
        setSelectionVars(optionDiv, adjustedColor)
      }

      const input = document.createElement('input')
      input.type = 'radio'
      const inputId = `${element.name}_${idx}`
      input.id = inputId
      input.name = element.name
      input.value = option.value
      input.checked = option.value === selectedValue
      input.dataset.elementType = 'radio'
      if (idx === 0 && element.predicted) input.dataset.predicted = element.predicted
      if (idx === 0 && element.justification) input.dataset.justification = element.justification
      if (adjustedColor) input.style.accentColor = adjustedColor

      const optionLabel = document.createElement('label')
      optionLabel.htmlFor = inputId
      optionLabel.innerHTML = renderBasicMarkdown(stripHtmlTags(option.label))
      optionLabel.className = 'flex-grow cursor-pointer'

      // Make the entire option div clickable — clicks on padding/border area
      // must propagate to the radio input, not silently die on the div.
      optionDiv.addEventListener('click', (e) => {
        if (e.target !== input) {
          input.checked = true
          input.dispatchEvent(new Event('change', { bubbles: true }))
        }
      })

      optionDiv.appendChild(input)
      optionDiv.appendChild(optionLabel)
      wrapper.appendChild(optionDiv)
    })
  } else {
    const errorP = document.createElement('p')
    errorP.className = 'text-sm text-red-600'
    errorP.textContent = `Error: No valid options for radio group '${element.name}'.`
    wrapper.appendChild(errorP)
  }
}

// ---------------------------------------------------------------------------
// New interactive element renderers
// ---------------------------------------------------------------------------

function renderDropdownElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  adjustedColor: string | null,
): void {
  const label = document.createElement('label')
  label.className = 'geems-label'
  label.textContent = stripPlaceholders(element.label || element.name)
  label.htmlFor = element.name
  if (adjustedColor) label.style.color = adjustedColor
  wrapper.appendChild(label)

  const select = document.createElement('select')
  select.className = 'geems-dropdown'
  select.id = element.name
  select.name = element.name
  select.dataset.elementType = 'dropdown'

  const { options } = parseRadioOptions(element)
  const selectedValue = element.predicted || element.value || (options.length > 0 ? options[0].value : '')

  options.forEach((opt) => {
    const option = document.createElement('option')
    option.value = opt.value
    option.textContent = stripHtmlTags(opt.label)
    if (opt.value === selectedValue) option.selected = true
    select.appendChild(option)
  })

  if (element.predicted) select.dataset.predicted = element.predicted
  if (element.justification) select.dataset.justification = element.justification
  wrapper.appendChild(select)
}

function renderRatingElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  adjustedColor: string | null,
): void {
  const label = document.createElement('label')
  label.className = 'geems-label'
  label.textContent = stripPlaceholders(element.label || element.name)
  if (adjustedColor) label.style.color = adjustedColor
  wrapper.appendChild(label)

  const maxStars = element.max != null && parseInt(String(element.max)) > 0 ? parseInt(String(element.max)) : 5
  const predictedVal = element.predicted ? parseInt(element.predicted) : NaN
  const defaultVal = !isNaN(predictedVal) ? predictedVal : (parseInt(element.value) || 0)

  const ratingContainer = document.createElement('div')
  ratingContainer.className = 'geems-rating'
  ratingContainer.dataset.elementType = 'rating'
  ratingContainer.dataset.name = element.name
  ratingContainer.dataset.value = String(defaultVal)
  if (element.predicted) ratingContainer.dataset.predicted = element.predicted
  if (element.justification) ratingContainer.dataset.justification = element.justification

  for (let i = 1; i <= maxStars; i++) {
    const star = document.createElement('span')
    star.className = `geems-rating-star${i <= defaultVal ? ' active' : ''}`
    star.dataset.value = String(i)
    star.textContent = '\u2605'
    if (adjustedColor && i <= defaultVal) star.style.color = adjustedColor

    star.addEventListener('click', () => {
      ratingContainer.dataset.value = String(i)
      ratingContainer.querySelectorAll('.geems-rating-star').forEach((s, idx) => {
        s.classList.toggle('active', idx < i)
        if (adjustedColor) (s as HTMLElement).style.color = idx < i ? adjustedColor : ''
      })
    })

    star.addEventListener('mouseenter', () => {
      ratingContainer.querySelectorAll('.geems-rating-star').forEach((s, idx) => {
        s.classList.toggle('hover', idx < i)
      })
    })

    ratingContainer.appendChild(star)
  }

  ratingContainer.addEventListener('mouseleave', () => {
    ratingContainer.querySelectorAll('.geems-rating-star').forEach(s => {
      s.classList.remove('hover')
    })
  })

  wrapper.appendChild(ratingContainer)
}

function renderToggleElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  adjustedColor: string | null,
): void {
  wrapper.classList.remove('geems-element')
  wrapper.style.borderLeftColor = 'transparent'
  wrapper.style.padding = '0'
  wrapper.style.marginBottom = '0.75rem'

  const toggleDiv = document.createElement('div')
  toggleDiv.className = 'geems-toggle-container'
  if (adjustedColor) {
    toggleDiv.style.borderColor = hexToRgba(adjustedColor, 0.25)
    toggleDiv.style.backgroundColor = hexToRgba(adjustedColor, isCurrentThemeLight() ? 0.05 : 0.08)
  }

  const label = document.createElement('span')
  label.className = 'geems-toggle-label'
  label.textContent = stripPlaceholders(element.label || element.name)

  const input = document.createElement('input')
  input.type = 'checkbox'
  input.className = 'geems-toggle-input'
  input.id = element.name
  input.name = element.name
  const checkValue = element.predicted || element.value
  input.checked = checkValue === true as unknown || String(checkValue).toLowerCase() === 'true'
  input.dataset.elementType = 'toggle'
  if (element.predicted) input.dataset.predicted = element.predicted
  if (element.justification) input.dataset.justification = element.justification

  const toggle = document.createElement('span')
  toggle.className = 'geems-toggle-switch'
  if (adjustedColor) toggle.style.setProperty('--toggle-active-color', adjustedColor)

  const labelWrapper = document.createElement('label')
  labelWrapper.className = 'geems-toggle-wrapper'
  labelWrapper.htmlFor = element.name
  labelWrapper.appendChild(input)
  labelWrapper.appendChild(toggle)

  toggleDiv.appendChild(label)
  toggleDiv.appendChild(labelWrapper)
  wrapper.appendChild(toggleDiv)
}

function renderButtonGroupElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  adjustedColor: string | null,
): void {
  const label = document.createElement('label')
  label.className = 'geems-label'
  label.textContent = stripPlaceholders(element.label || element.name)
  if (adjustedColor) label.style.color = adjustedColor
  wrapper.appendChild(label)

  const grid = document.createElement('div')
  grid.className = 'geems-button-group'
  grid.dataset.elementType = 'button_group'
  grid.dataset.name = element.name
  grid.dataset.value = element.predicted || element.value || ''
  if (element.predicted) grid.dataset.predicted = element.predicted
  if (element.justification) grid.dataset.justification = element.justification

  const { options } = parseRadioOptions(element)
  const selectedValue = element.predicted || element.value || ''

  options.forEach((opt) => {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = `geems-group-btn${opt.value === selectedValue ? ' active' : ''}`
    btn.innerHTML = renderBasicMarkdown(stripHtmlTags(opt.label))
    btn.dataset.value = opt.value
    if (adjustedColor && opt.value === selectedValue) {
      btn.style.backgroundColor = adjustedColor
      btn.style.borderColor = adjustedColor
      btn.style.color = contrastTextForBg(adjustedColor)
    }

    btn.addEventListener('click', () => {
      grid.dataset.value = opt.value
      grid.querySelectorAll('.geems-group-btn').forEach(b => {
        b.classList.remove('active')
        ;(b as HTMLElement).style.backgroundColor = ''
        ;(b as HTMLElement).style.borderColor = ''
        ;(b as HTMLElement).style.color = ''
      })
      btn.classList.add('active')
      if (adjustedColor) {
        btn.style.backgroundColor = adjustedColor
        btn.style.borderColor = adjustedColor
        btn.style.color = contrastTextForBg(adjustedColor)
      }
    })

    grid.appendChild(btn)
  })

  wrapper.appendChild(grid)
}

function renderMeterElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  adjustedColor: string | null,
): void {
  const label = document.createElement('label')
  label.className = 'geems-label'
  label.textContent = stripPlaceholders(element.label || element.name)
  if (adjustedColor) label.style.color = adjustedColor
  wrapper.appendChild(label)

  const min = parseFloat(String(element.min)) || 0
  const max = parseFloat(String(element.max)) || 100
  const val = parseFloat(element.value) || 0
  const pct = Math.max(0, Math.min(100, ((val - min) / (max - min)) * 100))

  const meterOuter = document.createElement('div')
  meterOuter.className = 'geems-meter'

  const meterFill = document.createElement('div')
  meterFill.className = 'geems-meter-fill'
  meterFill.style.width = '0%'
  if (adjustedColor) meterFill.style.background = adjustedColor

  const meterValue = document.createElement('span')
  meterValue.className = 'geems-meter-value'
  meterValue.textContent = String(val)

  meterOuter.appendChild(meterFill)
  meterOuter.appendChild(meterValue)
  wrapper.appendChild(meterOuter)

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      meterFill.style.width = `${pct}%`
    })
  })
}

function renderNumberInputElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  adjustedColor: string | null,
): void {
  const label = document.createElement('label')
  label.className = 'geems-label'
  label.textContent = stripPlaceholders(element.label || element.name)
  label.htmlFor = element.name
  if (adjustedColor) label.style.color = adjustedColor
  wrapper.appendChild(label)

  const inputGroup = document.createElement('div')
  inputGroup.className = 'geems-number-group'

  const min = parseFloat(String(element.min)) || 0
  const max = parseFloat(String(element.max)) || 100
  const step = parseFloat(String(element.step)) || 1

  const btnMinus = document.createElement('button')
  btnMinus.type = 'button'
  btnMinus.className = 'geems-number-btn'
  btnMinus.textContent = '\u2212'

  const input = document.createElement('input')
  input.type = 'number'
  input.className = 'geems-number-input'
  input.id = element.name
  input.name = element.name
  input.min = String(min)
  input.max = String(max)
  input.step = String(step)
  const predictedVal = element.predicted ? parseFloat(element.predicted) : NaN
  const defaultVal = !isNaN(predictedVal) ? predictedVal : parseFloat(element.value)
  input.value = String(isNaN(defaultVal) ? min : Math.max(min, Math.min(max, defaultVal)))
  input.dataset.elementType = 'number_input'
  if (element.predicted) input.dataset.predicted = element.predicted
  if (element.justification) input.dataset.justification = element.justification

  const btnPlus = document.createElement('button')
  btnPlus.type = 'button'
  btnPlus.className = 'geems-number-btn'
  btnPlus.textContent = '+'

  btnMinus.addEventListener('click', () => {
    const cur = parseFloat(input.value)
    if (cur - step >= min) input.value = String(cur - step)
  })
  btnPlus.addEventListener('click', () => {
    const cur = parseFloat(input.value)
    if (cur + step <= max) input.value = String(cur + step)
  })

  if (adjustedColor) {
    btnMinus.style.color = adjustedColor
    btnPlus.style.color = adjustedColor
  }

  inputGroup.appendChild(btnMinus)
  inputGroup.appendChild(input)
  inputGroup.appendChild(btnPlus)
  wrapper.appendChild(inputGroup)
}

function renderEmojiReactElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  adjustedColor: string | null,
): void {
  const label = document.createElement('label')
  label.className = 'geems-label'
  label.textContent = stripPlaceholders(element.label || element.name)
  if (adjustedColor) label.style.color = adjustedColor
  wrapper.appendChild(label)

  const emojiRow = document.createElement('div')
  emojiRow.className = 'geems-emoji-row'
  emojiRow.dataset.elementType = 'emoji_react'
  emojiRow.dataset.name = element.name
  emojiRow.dataset.value = element.predicted || ''
  if (element.predicted) emojiRow.dataset.predicted = element.predicted
  if (element.justification) emojiRow.dataset.justification = element.justification

  let emojis: string[] = []
  if (Array.isArray(element.options)) {
    emojis = (element.options as unknown[]).map(opt => {
      if (typeof opt === 'string') return opt
      if (opt && typeof opt === 'object') {
        const o = opt as Record<string, unknown>
        return String(o.emoji ?? o.value ?? o.label ?? '')
      }
      return String(opt)
    }).filter(e => e && !e.includes('[object'))
  } else if (typeof element.options === 'string') {
    try { emojis = JSON.parse(element.options) } catch { emojis = element.options.split(/\s+/) }
  }
  if (emojis.length === 0) emojis = ['\ud83d\ude0a', '\ud83d\ude22', '\ud83d\ude21', '\ud83d\ude31', '\ud83e\udd14', '\u2764\ufe0f']

  emojis.forEach(emoji => {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = `geems-emoji-btn${emoji === (element.predicted || '') ? ' active' : ''}`
    btn.textContent = emoji
    btn.dataset.value = emoji

    btn.addEventListener('click', () => {
      emojiRow.dataset.value = emoji
      emojiRow.querySelectorAll('.geems-emoji-btn').forEach(b => b.classList.remove('active'))
      btn.classList.add('active')
    })

    emojiRow.appendChild(btn)
  })

  wrapper.appendChild(emojiRow)
}

function renderColorPickElement(
  wrapper: HTMLDivElement,
  element: UIElement,
  adjustedColor: string | null,
): void {
  const label = document.createElement('label')
  label.className = 'geems-label'
  label.textContent = stripPlaceholders(element.label || element.name)
  if (adjustedColor) label.style.color = adjustedColor
  wrapper.appendChild(label)

  const colorGrid = document.createElement('div')
  colorGrid.className = 'geems-color-grid'
  colorGrid.dataset.elementType = 'color_pick'
  colorGrid.dataset.name = element.name
  colorGrid.dataset.value = element.predicted || ''
  if (element.predicted) colorGrid.dataset.predicted = element.predicted
  if (element.justification) colorGrid.dataset.justification = element.justification

  let colors: string[] = []
  if (Array.isArray(element.options)) {
    colors = (element.options as string[]).map(String)
  } else if (typeof element.options === 'string') {
    try { colors = JSON.parse(element.options) } catch { colors = element.options.split(/[,\s]+/).filter(Boolean) }
  }
  if (colors.length === 0) {
    colors = ['#e63946', '#f4a261', '#e9c46a', '#2a9d8f', '#264653', '#9b5de5', '#f4c2c2', '#b5e48c']
  }

  colors.forEach(color => {
    const swatch = document.createElement('button')
    swatch.type = 'button'
    swatch.className = `geems-color-swatch${color === (element.predicted || '') ? ' active' : ''}`
    swatch.style.background = color
    swatch.dataset.value = color
    swatch.title = color

    swatch.addEventListener('click', () => {
      colorGrid.dataset.value = color
      colorGrid.querySelectorAll('.geems-color-swatch').forEach(s => s.classList.remove('active'))
      swatch.classList.add('active')
    })

    colorGrid.appendChild(swatch)
  })

  wrapper.appendChild(colorGrid)
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Renders a JSON UI array from the LLM into the given container.
 *
 * Clears the container first, then creates DOM elements for each UIElement.
 * Hidden fields (notes, subjectId, gemini_facing_analysis) are not
 * rendered but returned in the RenderResult.
 */
export function renderUI(
  container: HTMLElement,
  uiJsonArray: UIElement[],
): RenderResult {
  const result: RenderResult = {
    notes: '',
    subjectId: '',
    analysis: '',
  }

  container.innerHTML = ''

  if (!Array.isArray(uiJsonArray)) {
    // Attempt to extract array from a wrapping object ({ ui: [...] })
    const wrapped = uiJsonArray as unknown as { ui?: UIElement[] }
    if (wrapped && typeof wrapped === 'object' && Array.isArray(wrapped.ui)) {
      uiJsonArray = wrapped.ui
    } else {
      console.error('Invalid UI data: Expected an array.', uiJsonArray)
      const errDiv = document.createElement('div')
      errDiv.className = 'error-message'
      errDiv.textContent = 'Invalid UI data format from API.'
      container.appendChild(errDiv)
      return result
    }
  }

  // Pending inline images to embed inside the next visible element
  let pendingInlineImages: HTMLDivElement[] = []

  // Cap images to prevent multi-image stacking (LLM sometimes returns 3-4)
  let mainImageCount = 0
  const MAX_MAIN_IMAGES = 1
  let inlineImageCount = 0
  const MAX_INLINE_IMAGES = 3

  for (let index = 0; index < uiJsonArray.length; index++) {
    const element = uiJsonArray[index]

    // ---- Extract hidden fields without rendering them ----
    // LLMs may use semantic names as either `type` or `name`, so check both.

    const elType = element.type
    const elName = element.name || ''

    // gemini_facing_analysis — always hidden
    if (elType === 'gemini_facing_analysis' || elName.includes('gemini_facing_analysis')) {
      result.analysis = element.text || element.value || ''
      continue
    }
    // notes — always hidden (unless it's an editable textfield)
    if ((elType === 'notes' || elName.includes('notes')) && elType !== 'textfield' && elType !== 'text_input') {
      result.notes = element.value || ''
      continue
    }
    // subjectId — always hidden
    if (elType === 'subjectId' || elName === 'subjectId') {
      result.subjectId = element.value || ''
      continue
    }
    // hidden elements
    if (elType === 'hidden') {
      if (elName === 'notes' || elName.includes('notes')) result.notes = element.value || ''
      else if (elName === 'subjectId') result.subjectId = element.value || ''
      continue
    }

    // ---- Inline images: collect now, embed inside next element (max 3) ----
    if (element.type === 'inline_image') {
      if (inlineImageCount >= MAX_INLINE_IMAGES) continue
      inlineImageCount++
      const imgWrapper = document.createElement('div')
      renderInlineImageElement(imgWrapper, element, null)
      pendingInlineImages.push(imgWrapper)
      continue
    }

    // ---- Render visible elements ----

    const wrapper = document.createElement('div')
    wrapper.className = 'geems-element'
    if (element.name) wrapper.dataset.elementName = element.name
    if (element.voice) {
      wrapper.classList.add(`voice-${element.voice}`)
      wrapper.dataset.voice = element.voice
    }

    let adjustedColor: string | null = null
    if (element.color && isValidHexColor(element.color)) {
      adjustedColor = adjustColorForContrast(element.color)
      wrapper.style.borderLeftColor = adjustedColor
      wrapper.style.borderColor = hexToRgba(adjustedColor, 0.25)
      wrapper.style.backgroundColor = hexToRgba(adjustedColor, isCurrentThemeLight() ? 0.05 : 0.08)
    } else {
      wrapper.style.borderLeftColor = 'transparent'
    }

    try {
      switch (element.type) {
        case 'image':
          if (mainImageCount >= MAX_MAIN_IMAGES) { continue }
          mainImageCount++
          renderImageElement(wrapper, element, adjustedColor)
          break
        case 'text':
        case 'narrative':
        case 'header':
          renderTextElement(wrapper, element, adjustedColor)
          break
        case 'textfield':
        case 'text_input':
          renderTextFieldElement(wrapper, element, adjustedColor)
          break
        case 'checkbox':
          renderCheckboxElement(wrapper, element, adjustedColor)
          break
        case 'slider':
          renderSliderElement(wrapper, element, adjustedColor)
          break
        case 'radio':
        case 'radio_group':
          renderRadioElement(wrapper, element, adjustedColor)
          break
        case 'dropdown':
        case 'select':
          renderDropdownElement(wrapper, element, adjustedColor)
          break
        case 'rating':
        case 'star_rating':
          renderRatingElement(wrapper, element, adjustedColor)
          break
        case 'toggle':
        case 'switch':
          renderToggleElement(wrapper, element, adjustedColor)
          break
        case 'button_group':
        case 'buttons':
          renderButtonGroupElement(wrapper, element, adjustedColor)
          break
        case 'meter':
        case 'progress':
        case 'gauge':
          renderMeterElement(wrapper, element, adjustedColor)
          break
        case 'number_input':
        case 'number':
          renderNumberInputElement(wrapper, element, adjustedColor)
          break
        case 'emoji_react':
        case 'emoji':
          renderEmojiReactElement(wrapper, element, adjustedColor)
          break
        case 'color_pick':
        case 'color':
          renderColorPickElement(wrapper, element, adjustedColor)
          break
        default:
          // LLMs invent types like "probe", "player_facing_analysis", "divine_wisdom".
          // If it has text/value content, render as text. Otherwise skip silently.
          if (element.text || element.value) {
            renderTextElement(wrapper, element, adjustedColor)
          } else {
            continue
          }
      }

      // Embed any pending inline images inside this element using flexbox
      if (pendingInlineImages.length > 0) {
        wrapper.classList.add('has-inline-image')
        // Wrap existing content in a flex child
        const contentDiv = document.createElement('div')
        contentDiv.className = 'geems-element-content'
        while (wrapper.firstChild) {
          contentDiv.appendChild(wrapper.firstChild)
        }
        wrapper.appendChild(contentDiv)
        // Append images column on the right
        const imgColumn = document.createElement('div')
        imgColumn.className = 'geems-inline-image-column'
        for (const imgDiv of pendingInlineImages) {
          imgColumn.appendChild(imgDiv)
        }
        wrapper.appendChild(imgColumn)
        pendingInlineImages = []
      }

      container.appendChild(wrapper)
    } catch (renderError) {
      console.error(
        `Error rendering element ${index} (type: ${element.type}, name: ${element.name}):`,
        renderError,
        element,
      )
      const errorWrapper = document.createElement('div')
      errorWrapper.className = 'geems-element error-message'
      errorWrapper.textContent = `Error rendering element: ${element.name || element.type}. Check console.`
      container.appendChild(errorWrapper)
    }
  }

  // If there are leftover inline images with no following element, attach to the last element
  if (pendingInlineImages.length > 0) {
    const lastEl = container.lastElementChild as HTMLElement | null
    if (lastEl) {
      lastEl.classList.add('has-inline-image')
      const contentDiv = document.createElement('div')
      contentDiv.className = 'geems-element-content'
      while (lastEl.firstChild) {
        contentDiv.appendChild(lastEl.firstChild)
      }
      lastEl.appendChild(contentDiv)
      const imgColumn = document.createElement('div')
      imgColumn.className = 'geems-inline-image-column'
      for (const imgDiv of pendingInlineImages) {
        imgColumn.appendChild(imgDiv)
      }
      lastEl.appendChild(imgColumn)
    }
  }

  return result
}

/**
 * Walks rendered interactive elements and extracts question context:
 * what was shown, why, predicted answers, and available options/ranges.
 */
export function collectQuestionContext(container: HTMLElement): QuestionContext[] {
  const contexts: QuestionContext[] = []
  const seen = new Set<string>()

  // Standard input elements (textfield, checkbox, slider, radio, dropdown, toggle, number_input)
  container.querySelectorAll<HTMLElement>('[data-element-type]').forEach((el) => {
    const inputEl = el as HTMLInputElement
    const name = inputEl.name
    const type = inputEl.dataset.elementType
    if (!name || !type || seen.has(name)) return
    // For radio, only process the first input (which carries predicted/justification)
    if (type === 'radio' && !inputEl.dataset.justification && !inputEl.dataset.predicted) return

    seen.add(name)
    const ctx: QuestionContext = { name, type }

    // Find label text from the closest wrapper
    const wrapper = el.closest('.geems-element')
    const labelEl = wrapper?.querySelector('.geems-label')
    if (labelEl) ctx.label = labelEl.textContent || undefined

    if (inputEl.dataset.justification) ctx.justification = inputEl.dataset.justification
    if (inputEl.dataset.predicted) ctx.predicted = inputEl.dataset.predicted

    // Extract options for radio
    if (type === 'radio' && wrapper) {
      const radios = wrapper.querySelectorAll<HTMLInputElement>(`input[name="${name}"]`)
      ctx.options = Array.from(radios).map(r => r.value)
    }

    // Extract range for slider / number_input
    if ((type === 'slider' || type === 'number_input') && inputEl.min && inputEl.max) {
      ctx.range = { min: parseFloat(inputEl.min), max: parseFloat(inputEl.max) }
    }

    contexts.push(ctx)
  })

  // Custom elements (rating, button_group, emoji_react, color_pick)
  container.querySelectorAll<HTMLElement>(
    '[data-element-type="rating"], [data-element-type="button_group"], [data-element-type="emoji_react"], [data-element-type="color_pick"]'
  ).forEach((el) => {
    const name = el.dataset.name
    const type = el.dataset.elementType
    if (!name || !type || seen.has(name)) return
    seen.add(name)

    const ctx: QuestionContext = { name, type }

    const wrapper = el.closest('.geems-element')
    const labelEl = wrapper?.querySelector('.geems-label')
    if (labelEl) ctx.label = labelEl.textContent || undefined

    if (el.dataset.justification) ctx.justification = el.dataset.justification
    if (el.dataset.predicted) ctx.predicted = el.dataset.predicted

    // Extract options for button_group
    if (type === 'button_group') {
      ctx.options = Array.from(el.querySelectorAll<HTMLElement>('[data-value]')).map(b => b.dataset.value!)
    }

    contexts.push(ctx)
  })

  // Extract options for dropdown (standard input element, but options are in <option> children)
  container.querySelectorAll<HTMLSelectElement>('[data-element-type="dropdown"]').forEach((sel) => {
    const existing = contexts.find(c => c.name === sel.name)
    if (existing) {
      existing.options = Array.from(sel.options).map(o => o.value)
    }
  })

  return contexts
}

/**
 * Collects values from all interactive input elements within the container
 * and returns a JSON string of the form `{ name: value, ..., turn: N }`.
 */
export function collectInputState(container: HTMLElement, turnNumber: number): string {
  const inputs: Record<string, string | number | boolean> = {}

  container.querySelectorAll<HTMLElement>('[data-element-type]').forEach((el) => {
    const inputEl = el as HTMLInputElement | HTMLTextAreaElement
    const name = inputEl.name
    if (!name) return

    const type = inputEl.dataset.elementType
    switch (type) {
      case 'textfield':
        // If left blank, use the predicted value as the player's response
        inputs[name] = inputEl.value.trim() || inputEl.dataset.predicted || ''
        break
      case 'checkbox':
        inputs[name] = (inputEl as HTMLInputElement).checked
        break
      case 'slider':
        inputs[name] = parseFloat(inputEl.value)
        break
      case 'radio':
        if ((inputEl as HTMLInputElement).checked) {
          inputs[name] = inputEl.value
        }
        break
      case 'dropdown':
        inputs[name] = inputEl.value
        break
      case 'number_input':
        inputs[name] = parseFloat(inputEl.value)
        break
      case 'toggle':
        inputs[name] = (inputEl as HTMLInputElement).checked
        break
    }

    // Capture justification alongside the value (hidden context for LLM)
    if (inputEl.dataset.justification && !inputs[`${name}__justification`]) {
      inputs[`${name}__justification`] = inputEl.dataset.justification
    }

    // Track whether player kept the LLM's predicted default
    if (inputEl.dataset.predicted && inputs[name] !== undefined) {
      const predicted = inputEl.dataset.predicted
      const val = inputs[name]
      const match =
        type === 'checkbox' || type === 'toggle'
          ? String(val) === String(predicted).toLowerCase() // "true"/"false"
          : type === 'slider' || type === 'number_input'
            ? parseFloat(String(val)) === parseFloat(predicted)
            : String(val) === predicted
      if (match) inputs[`${name}__usedDefault`] = 'true'
    }
  })

  // Collect from custom interactive elements (rating, button_group, emoji_react, color_pick)
  container.querySelectorAll<HTMLElement>(
    '[data-element-type="rating"], [data-element-type="button_group"], [data-element-type="emoji_react"], [data-element-type="color_pick"]'
  ).forEach((el) => {
    const name = el.dataset.name
    if (name && el.dataset.value !== undefined) {
      if (el.dataset.elementType === 'rating') {
        inputs[name] = parseInt(el.dataset.value) || 0
      } else {
        inputs[name] = el.dataset.value
      }
      // Capture justification for custom elements too
      if (el.dataset.justification) {
        inputs[`${name}__justification`] = el.dataset.justification
      }
      // Track whether player kept the LLM's predicted default
      if (el.dataset.predicted) {
        const predicted = el.dataset.predicted
        const val = el.dataset.elementType === 'rating'
          ? String(parseInt(el.dataset.value) || 0)
          : el.dataset.value
        if (val === predicted) inputs[`${name}__usedDefault`] = 'true'
      }
    }
  })

  inputs['turn'] = turnNumber

  return JSON.stringify(inputs)
}

// ---------------------------------------------------------------------------
// Reactive variant system — swaps text when player interacts with inputs
// ---------------------------------------------------------------------------

/**
 * Bucket a slider value (0–1 normalized) into "low" / "mid" / "high".
 */
function bucketSliderValue(value: number, min: number, max: number): string {
  const range = max - min
  if (range <= 0) return 'mid'
  const pct = (value - min) / range
  if (pct <= 0.3) return 'low'
  if (pct >= 0.7) return 'high'
  return 'mid'
}

/**
 * Cross-fade swap: fade out → replace innerHTML → fade in.
 */
function crossFadeSwap(textEl: HTMLElement, newHTML: string): void {
  // Cancel any active typewriter on this element first
  const tw = activeTypewriters.get(textEl)
  if (tw) tw.cancel()

  textEl.classList.add('geems-text-fading-out')
  setTimeout(() => {
    textEl.innerHTML = newHTML
    textEl.classList.remove('geems-text-fading-out')
    textEl.classList.add('geems-text-fading-in')
    setTimeout(() => {
      textEl.classList.remove('geems-text-fading-in')
    }, 300)
  }, 200)
}

/**
 * Attaches event listeners that swap reactive text variants when the player
 * interacts with the corresponding input element.
 *
 * Returns a cleanup function that removes all listeners.
 */
export function attachReactiveListeners(container: HTMLElement): () => void {
  const cleanups: (() => void)[] = []

  // Find all reactive wrappers
  const reactiveWrappers = container.querySelectorAll<HTMLElement>('[data-reactive-depends-on]')
  if (reactiveWrappers.length === 0) return () => {}

  // Group reactive elements by their depends_on source
  const dependencyMap = new Map<string, HTMLElement[]>()
  reactiveWrappers.forEach((wrapper) => {
    const source = wrapper.dataset.reactiveDependsOn!
    if (!dependencyMap.has(source)) dependencyMap.set(source, [])
    dependencyMap.get(source)!.push(wrapper)
  })

  for (const [sourceName, dependents] of dependencyMap) {
    // Find the source interactive element by matching data-element-name on the wrapper
    const sourceWrapper = container.querySelector<HTMLElement>(`[data-element-name="${sourceName}"]`)
    if (!sourceWrapper) continue

    // Determine source type from the interactive element inside
    const radioInputs = sourceWrapper.querySelectorAll<HTMLInputElement>(`input[type="radio"][name="${sourceName}"]`)
    const sliderInput = sourceWrapper.querySelector<HTMLInputElement>(`input[type="range"][name="${sourceName}"]`)
    const checkboxInput = sourceWrapper.querySelector<HTMLInputElement>(`input[type="checkbox"][name="${sourceName}"]`)
    const toggleInput = sourceWrapper.querySelector<HTMLInputElement>(`.geems-toggle-input[name="${sourceName}"]`)
    const dropdownInput = sourceWrapper.querySelector<HTMLSelectElement>(`select[name="${sourceName}"]`)
    const buttonGroup = sourceWrapper.querySelector<HTMLElement>('[data-element-type="button_group"]')
    const emojiReact = sourceWrapper.querySelector<HTMLElement>('[data-element-type="emoji_react"]')
    const colorPick = sourceWrapper.querySelector<HTMLElement>('[data-element-type="color_pick"]')

    function applyVariant(variantKey: string): void {
      for (const wrapper of dependents) {
        const variantsJson = wrapper.dataset.reactiveVariants
        const originalText = wrapper.dataset.reactiveOriginal || ''
        if (!variantsJson) continue

        let variants: Record<string, string>
        try { variants = JSON.parse(variantsJson) } catch { continue }

        const textEl = wrapper.querySelector<HTMLElement>('.geems-text')
        if (!textEl) continue

        const newText = variants[variantKey] ?? originalText
        crossFadeSwap(textEl, renderBasicMarkdown(newText))
      }
    }

    // Radio inputs — listen for click on each
    if (radioInputs.length > 0) {
      radioInputs.forEach((radio) => {
        const handler = () => { if (radio.checked) applyVariant(radio.value) }
        radio.addEventListener('change', handler)
        cleanups.push(() => radio.removeEventListener('change', handler))
      })
    }

    // Slider — bucket to low/mid/high
    if (sliderInput) {
      const handler = () => {
        const min = parseFloat(sliderInput.min) || 0
        const max = parseFloat(sliderInput.max) || 100
        const val = parseFloat(sliderInput.value) || 0
        applyVariant(bucketSliderValue(val, min, max))
      }
      sliderInput.addEventListener('input', handler)
      cleanups.push(() => sliderInput.removeEventListener('input', handler))
    }

    // Checkbox
    if (checkboxInput) {
      const handler = () => applyVariant(String(checkboxInput.checked))
      checkboxInput.addEventListener('change', handler)
      cleanups.push(() => checkboxInput.removeEventListener('change', handler))
    }

    // Toggle (uses same checkbox mechanism)
    if (toggleInput) {
      const handler = () => applyVariant(String(toggleInput.checked))
      toggleInput.addEventListener('change', handler)
      cleanups.push(() => toggleInput.removeEventListener('change', handler))
    }

    // Dropdown
    if (dropdownInput) {
      const handler = () => applyVariant(dropdownInput.value)
      dropdownInput.addEventListener('change', handler)
      cleanups.push(() => dropdownInput.removeEventListener('change', handler))
    }

    // Button group — listen for clicks on child buttons
    if (buttonGroup) {
      const buttons = buttonGroup.querySelectorAll<HTMLElement>('[data-value]')
      buttons.forEach((btn) => {
        const handler = () => applyVariant(btn.dataset.value || '')
        btn.addEventListener('click', handler)
        cleanups.push(() => btn.removeEventListener('click', handler))
      })
    }

    // Emoji react
    if (emojiReact) {
      const emojis = emojiReact.querySelectorAll<HTMLElement>('[data-value]')
      emojis.forEach((emoji) => {
        const handler = () => applyVariant(emoji.dataset.value || '')
        emoji.addEventListener('click', handler)
        cleanups.push(() => emoji.removeEventListener('click', handler))
      })
    }

    // Color pick
    if (colorPick) {
      const swatches = colorPick.querySelectorAll<HTMLElement>('[data-value]')
      swatches.forEach((swatch) => {
        const handler = () => applyVariant(swatch.dataset.value || '')
        swatch.addEventListener('click', handler)
        cleanups.push(() => swatch.removeEventListener('click', handler))
      })
    }
  }

  return () => { cleanups.forEach((fn) => fn()) }
}
